import React, { useState, useEffect } from 'react';
import {
  Shield,
  CheckCircle2,
  AlertTriangle,
  Clock,
  FileText,
  Link2,
  Search,
  Filter,
  Download,
  Plus,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Eye,
  Edit3,
  Trash2,
  ExternalLink,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Calendar,
  BarChart3,
  Database,
  RefreshCw,
  ArrowRight,
  Layers,
  GitBranch,
  Target,
  Lock,
  HelpCircle,
  Network,
  Sparkles,
  Award,
  Scale,
  Beaker,
  FileCheck,
  Building2,
  Globe,
  Leaf,
  TrendingUp,
  Zap,
  Info,
  GitMerge
} from 'lucide-react';
import LineageDrawer, { useLineageDrawer } from '../components/audit/LineageDrawer';
import { apiFetch } from '../services/api_service';


const API = process.env.REACT_APP_BACKEND_URL;

// Claim status configurations
const CLAIM_STATUS = {
  draft: { label: 'Draft', color: 'slate', icon: Edit3, bgClass: 'bg-slate-100', textClass: 'text-slate-700' },
  pending_review: { label: 'Pending Review', color: 'amber', icon: Clock, bgClass: 'bg-amber-100', textClass: 'text-amber-700' },
  approved: { label: 'Approved', color: 'green', icon: CheckCircle2, bgClass: 'bg-green-100', textClass: 'text-green-700' },
  published: { label: 'Published', color: 'blue', icon: Shield, bgClass: 'bg-blue-100', textClass: 'text-blue-700' },
  rejected: { label: 'Rejected', color: 'red', icon: XCircle, bgClass: 'bg-red-100', textClass: 'text-red-700' }
};

// Evidence type icons
const EVIDENCE_ICONS = {
  dataset: Database,
  document: FileText,
  audit_report: Shield,
  attestation: CheckCircle,
  calculation: BarChart3,
  lca_study: Beaker,
  third_party_cert: Award,
  supplier_cert: Building2
};

// EU Green Claims Directive - 9 Minimum Criteria
const GREEN_CLAIMS_CRITERIA = [
  { id: 'scientific', label: 'Scientific Evidence', description: 'Based on recognized scientific evidence', icon: Beaker },
  { id: 'lifecycle', label: 'Lifecycle Perspective', description: 'Considers full lifecycle impacts', icon: RefreshCw },
  { id: 'significant', label: 'Significant Aspects', description: 'Covers all significant environmental aspects', icon: Layers },
  { id: 'accurate', label: 'Accurate & Clear', description: 'Accurate for whole product/service', icon: Target },
  { id: 'superior', label: 'Above Common Practice', description: 'Performance exceeds legal minimums', icon: TrendingUp },
  { id: 'no_tradeoffs', label: 'No Hidden Trade-offs', description: 'Does not worsen other impacts', icon: Scale },
  { id: 'offset_transparent', label: 'Offset Transparency', description: 'Separates offsets from actual reductions', icon: Leaf },
  { id: 'third_party', label: 'Third-Party Verified', description: 'Independently verified by accredited body', icon: Award },
  { id: 'documented', label: 'Fully Documented', description: 'Complete audit trail maintained', icon: FileCheck }
];

// EU Green Claims Directive - Banned/Vague Terms
// These terms are prohibited without specific substantiation under the directive
const BANNED_TERMS = [
  { term: 'eco-friendly', severity: 'high', reason: 'Vague claim without specific environmental benefit' },
  { term: 'environmentally friendly', severity: 'high', reason: 'Generic claim prohibited under Art. 5' },
  { term: 'green', severity: 'medium', reason: 'Requires specific context and substantiation' },
  { term: 'sustainable', severity: 'medium', reason: 'Must specify what aspect is sustainable' },
  { term: 'climate neutral', severity: 'high', reason: 'Prohibited unless full lifecycle verified' },
  { term: 'carbon neutral', severity: 'high', reason: 'Must separate actual reductions from offsets' },
  { term: 'net zero', severity: 'high', reason: 'Requires SBTi-aligned pathway and verification' },
  { term: 'natural', severity: 'medium', reason: 'Misleading if product contains synthetic elements' },
  { term: 'biodegradable', severity: 'medium', reason: 'Must specify conditions and timeframe' },
  { term: 'recyclable', severity: 'low', reason: 'Must specify actual recyclability rates in target markets' },
  { term: 'eco', severity: 'medium', reason: 'Vague prefix without substantiation' },
  { term: 'planet-friendly', severity: 'high', reason: 'Generic claim prohibited under directive' },
  { term: 'earth-friendly', severity: 'high', reason: 'Generic claim prohibited under directive' },
  { term: 'clean', severity: 'medium', reason: 'Requires specific environmental context' },
  { term: 'low carbon', severity: 'low', reason: 'Must provide baseline comparison' },
  { term: 'reduced emissions', severity: 'low', reason: 'Must specify baseline, scope, and verification' },
  { term: 'carbon positive', severity: 'high', reason: 'Claims of net benefit require extensive verification' },
  { term: 'climate positive', severity: 'high', reason: 'Claims of net benefit require extensive verification' },
  { term: 'zero waste', severity: 'medium', reason: 'Must define waste categories and measurement' },
  { term: 'compostable', severity: 'low', reason: 'Must specify industrial vs home composting' }
];

// Function to check claim text for banned terms
const checkBannedTerms = (text) => {
  if (!text) return [];
  const lowerText = text.toLowerCase();
  const foundTerms = [];
  
  BANNED_TERMS.forEach(banned => {
    if (lowerText.includes(banned.term.toLowerCase())) {
      foundTerms.push({
        ...banned,
        position: lowerText.indexOf(banned.term.toLowerCase())
      });
    }
  });
  
  return foundTerms.sort((a, b) => {
    const severityOrder = { high: 0, medium: 1, low: 2 };
    return severityOrder[a.severity] - severityOrder[b.severity];
  });
};

// Sample claims data with enhanced fields
const SAMPLE_CLAIMS = [
  {
    id: 'CLM-001',
    title: 'Total GHG Emissions (Scope 1 + 2)',
    claim_text: 'In FY2024, the company emitted 251,548 tCO2e across Scope 1 and Scope 2 emissions, representing an 8.3% reduction from the prior year.',
    category: 'Environmental',
    frameworks: ['ESRS E1', 'GRI 305', 'CDP Climate', 'TCFD'],
    status: 'approved',
    kpi_value: '251,548 tCO2e',
    kpi_definition: 'Total Scope 1 + 2 GHG Emissions',
    period_start: '2024-01-01',
    period_end: '2024-12-31',
    evidence_count: 4,
    evidence_strength: 'strong',
    updated_at: '2024-12-15',
    owner_name: 'Sustainability Team',
    reviewer_name: 'Legal Compliance',
    requirements: ['ESRS E1-6', 'GRI 305-1', 'GRI 305-2'],
    green_claims_score: 8,
    criteria_met: ['scientific', 'lifecycle', 'significant', 'accurate', 'superior', 'no_tradeoffs', 'third_party', 'documented'],
    verification_status: 'verified',
    verifier: 'KPMG Assurance',
    data_freshness: '2024-12',
    linked_modules: ['requirements_tracker', 'audit_pack', 'dashboard']
  },
  {
    id: 'CLM-002',
    title: 'Workforce Diversity',
    claim_text: 'As of December 31, 2024, women represent 42% of our total workforce and 35% of management positions.',
    category: 'Social',
    frameworks: ['ESRS S1', 'GRI 405'],
    status: 'pending_review',
    kpi_value: '42% / 35%',
    kpi_definition: 'Gender Diversity Metrics',
    period_start: '2024-01-01',
    period_end: '2024-12-31',
    evidence_count: 2,
    evidence_strength: 'medium',
    updated_at: '2024-12-18',
    owner_name: 'HR Analytics',
    reviewer_name: 'Board Diversity Committee',
    requirements: ['ESRS S1-9', 'GRI 405-1'],
    green_claims_score: 5,
    criteria_met: ['scientific', 'accurate', 'documented', 'no_tradeoffs', 'significant'],
    verification_status: 'pending',
    verifier: null,
    data_freshness: '2024-12',
    linked_modules: ['requirements_tracker', 'hr_system']
  },
  {
    id: 'CLM-003',
    title: 'EU Taxonomy Alignment - Climate Mitigation',
    claim_text: 'In FY2024, 34% of revenue, 28% of CapEx, and 22% of OpEx were aligned with EU Taxonomy climate change mitigation objectives.',
    category: 'Governance',
    frameworks: ['EU Taxonomy Art. 8', 'CSRD'],
    status: 'draft',
    kpi_value: '34% / 28% / 22%',
    kpi_definition: 'Taxonomy Alignment KPIs',
    period_start: '2024-01-01',
    period_end: '2024-12-31',
    evidence_count: 6,
    evidence_strength: 'strong',
    updated_at: '2024-12-20',
    owner_name: 'Finance Team',
    reviewer_name: 'External Auditor',
    requirements: ['Taxonomy Regulation Art. 8', 'CSRD Art. 29b'],
    green_claims_score: 7,
    criteria_met: ['scientific', 'lifecycle', 'accurate', 'superior', 'no_tradeoffs', 'documented', 'significant'],
    verification_status: 'not_started',
    verifier: null,
    data_freshness: '2024-12',
    linked_modules: ['requirements_tracker', 'taxonomy_calculator', 'xbrl_export']
  },
  {
    id: 'CLM-004',
    title: 'Supply Chain Human Rights Due Diligence',
    claim_text: 'We conducted human rights due diligence on 100% of Tier 1 suppliers and 78% of Tier 2 suppliers in high-risk countries during FY2024.',
    category: 'Social',
    frameworks: ['CSDDD Art. 6', 'ESRS S2', 'UN Guiding Principles'],
    status: 'approved',
    kpi_value: '100% / 78%',
    kpi_definition: 'Supplier DD Coverage',
    period_start: '2024-01-01',
    period_end: '2024-12-31',
    evidence_count: 8,
    evidence_strength: 'strong',
    updated_at: '2024-12-10',
    owner_name: 'Procurement',
    reviewer_name: 'Legal Compliance',
    requirements: ['CSDDD Art. 6', 'ESRS S2-1'],
    green_claims_score: 9,
    criteria_met: ['scientific', 'lifecycle', 'significant', 'accurate', 'superior', 'no_tradeoffs', 'third_party', 'documented', 'offset_transparent'],
    verification_status: 'verified',
    verifier: 'EcoVadis',
    data_freshness: '2024-12',
    linked_modules: ['requirements_tracker', 'value_chain', 'supplier_risk', 'audit_pack']
  },
  {
    id: 'CLM-005',
    title: 'Water Consumption Intensity',
    claim_text: 'Water consumption intensity decreased by 12% to 3.2 m³ per unit of production in FY2024.',
    category: 'Environmental',
    frameworks: ['ESRS E3', 'GRI 303', 'CDP Water'],
    status: 'published',
    kpi_value: '3.2 m³/unit',
    kpi_definition: 'Water Intensity',
    period_start: '2024-01-01',
    period_end: '2024-12-31',
    evidence_count: 3,
    evidence_strength: 'strong',
    updated_at: '2024-11-30',
    owner_name: 'Operations',
    reviewer_name: 'Sustainability Team',
    requirements: ['ESRS E3-4', 'GRI 303-3'],
    green_claims_score: 8,
    criteria_met: ['scientific', 'lifecycle', 'accurate', 'superior', 'no_tradeoffs', 'third_party', 'documented', 'significant'],
    verification_status: 'verified',
    verifier: 'Bureau Veritas',
    data_freshness: '2024-11',
    linked_modules: ['requirements_tracker', 'dashboard', 'audit_pack', 'impact_engine']
  }
];

// Sample evidence data
const SAMPLE_EVIDENCE = {
  'CLM-001': [
    { id: 'EV-001', type: 'dataset', title: 'Emissions Inventory 2024', source: 'GHG Protocol Calculator', confidence_score: 98, date: '2024-12-01' },
    { id: 'EV-002', type: 'audit_report', title: 'Third-Party Verification Report', source: 'KPMG Assurance', confidence_score: 100, date: '2024-12-10' },
    { id: 'EV-003', type: 'document', title: 'Energy Consumption Records', source: 'Utility Invoices', confidence_score: 95, date: '2024-11-30' },
    { id: 'EV-004', type: 'calculation', title: 'Emission Factor Calculations', source: 'Internal Model', confidence_score: 90, date: '2024-12-05' }
  ],
  'CLM-004': [
    { id: 'EV-010', type: 'audit_report', title: 'Supplier Audit Reports', source: 'Third-Party Auditor', confidence_score: 95, date: '2024-11-15' },
    { id: 'EV-011', type: 'attestation', title: 'Supplier Self-Assessments', source: 'Supplier Portal', confidence_score: 75, date: '2024-10-30' },
    { id: 'EV-012', type: 'dataset', title: 'Supplier Risk Database', source: 'Internal System', confidence_score: 90, date: '2024-12-01' },
    { id: 'EV-013', type: 'third_party_cert', title: 'EcoVadis Certification', source: 'EcoVadis Platform', confidence_score: 98, date: '2024-11-20' }
  ],
  'CLM-005': [
    { id: 'EV-020', type: 'dataset', title: 'Water Meter Readings', source: 'IoT Sensors', confidence_score: 99, date: '2024-11-30' },
    { id: 'EV-021', type: 'lca_study', title: 'Water Lifecycle Assessment', source: 'Consultant Report', confidence_score: 92, date: '2024-10-15' },
    { id: 'EV-022', type: 'third_party_cert', title: 'Bureau Veritas Verification', source: 'BV Portal', confidence_score: 100, date: '2024-11-25' }
  ]
};

// Module connection definitions
const MODULE_CONNECTIONS = {
  requirements_tracker: { name: 'Requirements Tracker', icon: Target, color: 'indigo', path: '/obligations-tracker' },
  audit_pack: { name: 'Audit Pack Builder', icon: FileCheck, color: 'green', path: '/audit-pack' },
  dashboard: { name: 'Executive Dashboard', icon: BarChart3, color: 'blue', path: '/dashboard' },
  value_chain: { name: 'Value Chain Risk', icon: GitBranch, color: 'amber', path: '/value-chain' },
  supplier_risk: { name: 'Supplier Risk', icon: Building2, color: 'orange', path: '/supplier-risk' },
  taxonomy_calculator: { name: 'EU Taxonomy', icon: Leaf, color: 'emerald', path: '/eu-taxonomy' },
  xbrl_export: { name: 'XBRL Export', icon: FileText, color: 'purple', path: '/xbrl-export' },
  impact_engine: { name: 'Impact Engine', icon: Zap, color: 'rose', path: '/impact-engine' },
  hr_system: { name: 'HR Analytics', icon: User, color: 'cyan', path: '/hr' }
};

const ClaimsAssertions = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedClaim, setExpandedClaim] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const [activeTab, setActiveTab] = useState('claims'); // 'claims', 'analytics', 'intelligence'
  
  // Lineage drawer state
  const lineageDrawer = useLineageDrawer();

  // Fetch claims from API
  useEffect(() => {
    const fetchClaims = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        
        let url = `${API}/api/disclosure/claims`;
        const params = [];
        if (selectedStatus !== 'all') params.push(`status=${selectedStatus}`);
        if (selectedCategory !== 'all') params.push(`category=${selectedCategory}`);
        if (params.length > 0) url += `?${params.join('&')}`;
        
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            const claimsData = data.claims?.length > 0 ? data.claims : SAMPLE_CLAIMS;
            setClaims(claimsData);
          } else {
            setError(data.error || 'Failed to fetch claims');
          }
        } else {
          setError('Failed to fetch claims from server');
        }
      } catch (err) {
        console.error('Error fetching claims:', err);
        setClaims(SAMPLE_CLAIMS);
      } finally {
        setLoading(false);
      }
    };
    
    fetchClaims();
  }, [selectedCategory, selectedStatus]);

  // Filter claims
  const filteredClaims = claims.filter(claim => {
    if (selectedCategory !== 'all' && claim.category !== selectedCategory) return false;
    if (selectedStatus !== 'all' && claim.status !== selectedStatus) return false;
    if (searchTerm && !claim.title?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !claim.claim_text?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // Calculate statistics
  const stats = {
    total: claims.length,
    draft: claims.filter(c => c.status === 'draft').length,
    pending: claims.filter(c => c.status === 'pending_review').length,
    approved: claims.filter(c => c.status === 'approved').length,
    published: claims.filter(c => c.status === 'published').length,
    strongEvidence: claims.filter(c => c.evidence_strength === 'strong').length,
    verified: claims.filter(c => c.verification_status === 'verified').length,
    avgGreenScore: Math.round(claims.reduce((sum, c) => sum + (c.green_claims_score || 0), 0) / (claims.length || 1))
  };

  // Update claim status
  const updateClaimStatus = async (claimId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/disclosure/claims/${claimId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        setClaims(prev => prev.map(c => 
          c.id === claimId ? { ...c, status: newStatus, updated_at: new Date().toISOString().split('T')[0] } : c
        ));
      }
    } catch (err) {
      console.error('Error updating claim status:', err);
      setClaims(prev => prev.map(c => 
        c.id === claimId ? { ...c, status: newStatus, updated_at: new Date().toISOString().split('T')[0] } : c
      ));
    }
  };

  // Get evidence for a claim
  const getEvidenceForClaim = (claimId) => {
    return SAMPLE_EVIDENCE[claimId] || [];
  };

  // Format period display
  const formatPeriod = (claim) => {
    if (claim.period_start && claim.period_end) {
      const end = new Date(claim.period_end);
      return `FY${end.getFullYear()}`;
    }
    return claim.period || 'N/A';
  };

  // Render Green Claims Compliance Score
  const renderGreenClaimsScore = (claim) => {
    const score = claim.green_claims_score || 0;
    const criteriaMet = claim.criteria_met || [];
    const color = score >= 8 ? 'green' : score >= 5 ? 'amber' : 'red';
    
    return (
      <div className="flex flex-col items-center">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center bg-${color}-100 border-2 border-${color}-300`}>
          <span className={`text-lg font-bold text-${color}-700`}>{score}/9</span>
        </div>
        <span className="text-xs text-slate-500 mt-1">EU GCD Score</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="claims-loading">
        <RefreshCw className="animate-spin text-blue-600" size={32} />
        <span className="ml-3 text-slate-600">Loading claims...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="claims-assertions">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 rounded-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Shield size={32} />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Claims & Assertions Engine</h1>
              <p className="text-blue-100">EU Green Claims Directive compliant, audit-defensible statements</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button className="px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 flex items-center gap-2" data-testid="export-claims-btn">
              <Download size={18} />
              Export Claims
            </button>
            <button 
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-white text-blue-700 rounded-lg font-semibold hover:bg-blue-50 flex items-center gap-2"
              data-testid="new-claim-btn"
            >
              <Plus size={18} />
              New Claim
            </button>
          </div>
        </div>
      </div>

      {/* In-Page Guide */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl overflow-hidden">
        <button 
          onClick={() => setShowGuide(!showGuide)}
          className="w-full flex items-center justify-between p-4 hover:bg-indigo-100/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <HelpCircle className="h-5 w-5 text-indigo-600" />
            </div>
            <span className="font-semibold text-indigo-900">How to Use Claims Engine & Industry Best Practices</span>
          </div>
          {showGuide ? <ChevronUp className="h-5 w-5 text-indigo-600" /> : <ChevronDown className="h-5 w-5 text-indigo-600" />}
        </button>
        
        {showGuide && (
          <div className="px-4 pb-4 space-y-4">
            {/* Purpose */}
            <div className="bg-white rounded-lg border border-indigo-200 p-4">
              <h4 className="font-medium text-indigo-900 mb-2 flex items-center gap-2">
                <Shield className="h-4 w-4 text-indigo-600" />
                What is the Claims & Assertions Engine?
              </h4>
              <p className="text-sm text-slate-700">
                The Claims Engine transforms your ESG data into <strong>audit-defensible sustainability statements</strong>. 
                Each claim is validated against the <strong>EU Green Claims Directive's 9 minimum criteria</strong>, ensuring 
                your reports withstand regulatory scrutiny and third-party audits.
              </p>
            </div>

            {/* Workflow Steps */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              {[
                { step: 1, title: 'Link KPI Data', desc: 'Connect verified KPI values from your data sources' },
                { step: 2, title: 'Draft Claim', desc: 'Create precise, measurable claim statements' },
                { step: 3, title: 'Attach Evidence', desc: 'Link supporting documents and verifications' },
                { step: 4, title: 'GCD Compliance', desc: 'Validate against 9 EU criteria' },
                { step: 5, title: 'Publish & Lock', desc: 'Approve, publish, and lock for audit' }
              ].map(({ step, title, desc }) => (
                <div key={step} className="bg-white p-3 rounded-lg border border-indigo-200 shadow-sm">
                  <div className="flex items-center gap-2 text-indigo-600 font-medium mb-1">
                    <span className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold">{step}</span>
                    {title}
                  </div>
                  <p className="text-xs text-slate-600">{desc}</p>
                </div>
              ))}
            </div>

            {/* EU Green Claims Directive Criteria */}
            <div className="bg-white rounded-lg border border-indigo-200 p-4">
              <h4 className="font-medium text-indigo-900 mb-3 flex items-center gap-2">
                <Award className="h-4 w-4 text-indigo-600" />
                EU Green Claims Directive - 9 Minimum Criteria
              </h4>
              <div className="grid grid-cols-3 md:grid-cols-9 gap-2">
                {GREEN_CLAIMS_CRITERIA.map(criteria => {
                  const Icon = criteria.icon;
                  return (
                    <div key={criteria.id} className="text-center p-2 bg-slate-50 rounded-lg" title={criteria.description}>
                      <Icon size={16} className="mx-auto text-indigo-500 mb-1" />
                      <p className="text-xs text-slate-600 leading-tight">{criteria.label}</p>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500 mt-3 italic">
                Each claim is scored 0-9 based on criteria met. Claims scoring 8+ are considered audit-ready.
              </p>
            </div>

            {/* Module Connections */}
            <div className="bg-white rounded-lg border border-indigo-200 p-4">
              <h4 className="font-medium text-indigo-900 mb-3 flex items-center gap-2">
                <Network className="h-4 w-4 text-indigo-600" />
                Inter-Module Intelligence
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                  <p className="font-medium text-indigo-800 flex items-center gap-2">
                    <Target size={14} /> Requirements Tracker
                  </p>
                  <p className="text-slate-600 mt-1 text-xs">Claims satisfy specific ESRS/CSDDD requirements</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="font-medium text-green-800 flex items-center gap-2">
                    <FileCheck size={14} /> Audit Pack Builder
                  </p>
                  <p className="text-slate-600 mt-1 text-xs">Published claims auto-populate audit packs</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="font-medium text-amber-800 flex items-center gap-2">
                    <GitBranch size={14} /> Value Chain
                  </p>
                  <p className="text-slate-600 mt-1 text-xs">Supply chain DD claims link to supplier risk</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="font-medium text-purple-800 flex items-center gap-2">
                    <FileText size={14} /> XBRL Export
                  </p>
                  <p className="text-slate-600 mt-1 text-xs">Claims tagged for digital regulatory filings</p>
                </div>
              </div>
            </div>

            {/* Pro Tips */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>💡 Best Practice:</strong> Avoid generic terms like "eco-friendly" or "green" without certified excellence. 
                The EU bans offset-based neutrality claims (e.g., "carbon neutral"). Focus on <strong>specific, measurable, verified</strong> statements.
                All claims require <strong>third-party verification</strong> before publication under the Green Claims Directive.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Workflow Banner */}
      <div className="bg-slate-900 rounded-xl p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="text-sm text-slate-400">Defensibility Workflow:</span>
            {['Upload', 'Map', 'Evidence', 'Claim', 'Audit Pack'].map((step, idx) => (
              <React.Fragment key={step}>
                <div className={`flex items-center gap-2 ${idx === 3 ? 'text-blue-400 font-semibold' : 'text-slate-400'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                    idx === 3 ? 'bg-blue-500 text-white ring-2 ring-blue-300' : idx < 3 ? 'bg-green-500/20 text-green-400' : 'bg-slate-700'
                  }`}>
                    {idx < 3 ? <CheckCircle2 size={14} /> : idx + 1}
                  </div>
                  <span className="text-sm">{step}</span>
                </div>
                {idx < 4 && <ArrowRight size={16} className="text-slate-600" />}
              </React.Fragment>
            ))}
          </div>
          <span className="text-xs text-slate-500">You are here: Building defensible claims</span>
        </div>
      </div>

      {/* Enhanced Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
        <div className="bg-white rounded-xl shadow p-6 col-span-2" data-testid="claims-status-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900">Claim Status</h3>
            <span className="text-3xl font-bold text-blue-600">{stats.total}</span>
          </div>
          <div className="flex gap-2">
            {Object.entries(CLAIM_STATUS).slice(0, 4).map(([status, config]) => (
              <div key={status} className={`flex-1 text-center p-2 rounded-lg ${config.bgClass}`}>
                <p className={`text-lg font-bold ${config.textClass}`}>{stats[status === 'pending_review' ? 'pending' : status] || 0}</p>
                <p className="text-xs text-slate-600">{config.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-green-500" data-testid="strong-evidence-card">
          <div className="flex items-center gap-2 mb-1">
            <Shield size={18} className="text-green-600" />
            <span className="text-sm font-medium text-slate-600">Verified</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats.verified}</p>
          <p className="text-xs text-slate-500">3rd party certified</p>
        </div>

        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <Award size={18} />
            <span className="text-sm font-medium text-white/80">Avg GCD Score</span>
          </div>
          <p className="text-2xl font-bold">{stats.avgGreenScore}/9</p>
          <p className="text-xs text-white/70">EU compliance</p>
        </div>

        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-center gap-2 mb-1">
            <Link2 size={18} className="text-blue-600" />
            <span className="text-sm font-medium text-slate-600">Linked KPIs</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{claims.length}</p>
          <p className="text-xs text-slate-500">data points</p>
        </div>

        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-purple-500">
          <div className="flex items-center gap-2 mb-1">
            <Layers size={18} className="text-purple-600" />
            <span className="text-sm font-medium text-slate-600">Frameworks</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">6</p>
          <p className="text-xs text-slate-500">covered</p>
        </div>

        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-amber-500">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={18} className="text-amber-600" />
            <span className="text-sm font-medium text-slate-600">Needs Review</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats.pending + stats.draft}</p>
          <p className="text-xs text-slate-500">claims</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow">
        <div className="border-b border-slate-200">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('claims')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'claims'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <Shield size={18} />
                All Claims ({filteredClaims.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'analytics'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <BarChart3 size={18} />
                GCD Compliance Analytics
              </div>
            </button>
            <button
              onClick={() => setActiveTab('intelligence')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'intelligence'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <Network size={18} />
                Inter-Module Intelligence
              </div>
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Claims Tab */}
          {activeTab === 'claims' && (
            <>
              {/* Filters */}
              <div className="mb-6 flex flex-wrap items-center gap-4" data-testid="filters-section">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  data-testid="category-filter"
                >
                  <option value="all">All Categories</option>
                  <option value="Environmental">Environmental</option>
                  <option value="Social">Social</option>
                  <option value="Governance">Governance</option>
                </select>

                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  data-testid="status-filter"
                >
                  <option value="all">All Statuses</option>
                  {Object.entries(CLAIM_STATUS).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>

                <div className="flex-1 relative">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search claims..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm"
                    data-testid="search-input"
                  />
                </div>
              </div>

              {/* Claims List */}
              <div className="space-y-4" data-testid="claims-list">
                {filteredClaims.map(claim => {
                  const statusConfig = CLAIM_STATUS[claim.status] || CLAIM_STATUS.draft;
                  const StatusIcon = statusConfig.icon;
                  const isExpanded = expandedClaim === claim.id;
                  const evidence = getEvidenceForClaim(claim.id);
                  const greenScore = claim.green_claims_score || 0;
                  const scoreColor = greenScore >= 8 ? 'green' : greenScore >= 5 ? 'amber' : 'red';

                  return (
                    <div key={claim.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`claim-${claim.id}`}>
                      {/* Claim Header */}
                      <div 
                        className="p-4 cursor-pointer hover:bg-slate-50"
                        onClick={() => setExpandedClaim(isExpanded ? null : claim.id)}
                      >
                        <div className="flex items-start gap-4">
                          {/* Expand Toggle */}
                          <button className="mt-1 text-slate-400">
                            {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                          </button>

                          {/* Status Badge */}
                          <div className={`px-3 py-1 rounded-full ${statusConfig.bgClass} ${statusConfig.textClass} flex items-center gap-1 flex-shrink-0`}>
                            <StatusIcon size={14} />
                            <span className="text-xs font-medium">{statusConfig.label}</span>
                          </div>

                          {/* Claim Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-xs text-slate-500">{claim.id}</span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                claim.category === 'Environmental' ? 'bg-green-100 text-green-700' :
                                claim.category === 'Social' ? 'bg-purple-100 text-purple-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {claim.category}
                              </span>
                              {claim.verification_status === 'verified' && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700 flex items-center gap-1">
                                  <Award size={10} /> Verified
                                </span>
                              )}
                            </div>
                            <h3 className="font-semibold text-slate-900 mb-1">{claim.title}</h3>
                            <p className="text-sm text-slate-600 line-clamp-2">{claim.claim_text}</p>
                          </div>

                          {/* KPI Value */}
                          <div className="text-right flex-shrink-0">
                            <p className="text-lg font-bold text-slate-900">{claim.kpi_value || 'N/A'}</p>
                            <p className="text-xs text-slate-500">{formatPeriod(claim)}</p>
                          </div>

                          {/* GCD Score */}
                          <div className="flex flex-col items-center flex-shrink-0">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center bg-${scoreColor}-100 border-2 border-${scoreColor}-300`}>
                              <span className={`text-sm font-bold text-${scoreColor}-700`}>{greenScore}/9</span>
                            </div>
                            <span className="text-xs text-slate-500 mt-1">GCD Score</span>
                          </div>

                          {/* Evidence Strength */}
                          <div className="flex flex-col items-center flex-shrink-0">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              claim.evidence_strength === 'strong' ? 'bg-green-100 text-green-600' :
                              claim.evidence_strength === 'medium' ? 'bg-amber-100 text-amber-600' :
                              'bg-red-100 text-red-600'
                            }`}>
                              <Shield size={20} />
                            </div>
                            <span className="text-xs text-slate-500 mt-1">{claim.evidence_count || 0} items</span>
                          </div>

                          {/* Frameworks */}
                          <div className="flex-shrink-0 w-32">
                            <div className="flex flex-wrap gap-1">
                              {(claim.frameworks || []).slice(0, 2).map((fw, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                                  {fw}
                                </span>
                              ))}
                              {(claim.frameworks || []).length > 2 && (
                                <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                                  +{claim.frameworks.length - 2}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="border-t border-slate-200 bg-slate-50 p-4">
                          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                            {/* Claim Details */}
                            <div className="bg-white rounded-lg p-4 border border-slate-200">
                              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                <FileText size={16} />
                                Claim Details
                              </h4>
                              <div className="space-y-3">
                                <div>
                                  <p className="text-xs text-slate-500 mb-1">Full Claim Text</p>
                                  <p className="text-sm text-slate-700 bg-slate-50 p-2 rounded">{claim.claim_text}</p>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  <div>
                                    <p className="text-xs text-slate-500">KPI Definition</p>
                                    <p className="font-medium text-slate-700">{claim.kpi_definition || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-500">Reporting Period</p>
                                    <p className="font-medium text-slate-700">{formatPeriod(claim)}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-500">Verifier</p>
                                    <p className="font-medium text-slate-700">{claim.verifier || 'Pending'}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-500">Data Freshness</p>
                                    <p className="font-medium text-slate-700">{claim.data_freshness || 'N/A'}</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* EU GCD Criteria Checklist */}
                            <div className="bg-white rounded-lg p-4 border border-slate-200">
                              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                <Award size={16} />
                                EU GCD Criteria ({(claim.criteria_met || []).length}/9)
                              </h4>
                              <div className="space-y-1">
                                {GREEN_CLAIMS_CRITERIA.map(criteria => {
                                  const isMet = (claim.criteria_met || []).includes(criteria.id);
                                  return (
                                    <div key={criteria.id} className={`flex items-center gap-2 text-xs p-1.5 rounded ${isMet ? 'bg-green-50' : 'bg-slate-50'}`}>
                                      {isMet ? (
                                        <CheckCircle2 size={12} className="text-green-500" />
                                      ) : (
                                        <XCircle size={12} className="text-slate-300" />
                                      )}
                                      <span className={isMet ? 'text-green-700' : 'text-slate-500'}>{criteria.label}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Evidence Chain */}
                            <div className="bg-white rounded-lg p-4 border border-slate-200">
                              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                <Link2 size={16} />
                                Evidence Chain
                              </h4>
                              {evidence.length > 0 ? (
                                <div className="space-y-2">
                                  {evidence.map(ev => {
                                    const EvidenceIcon = EVIDENCE_ICONS[ev.type] || FileText;
                                    return (
                                      <div key={ev.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg group">
                                        <EvidenceIcon size={16} className="text-slate-500" />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-slate-700 truncate">{ev.title}</p>
                                          <p className="text-xs text-slate-500">{ev.source}</p>
                                        </div>
                                        <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                          ev.confidence_score >= 90 ? 'bg-green-100 text-green-700' :
                                          ev.confidence_score >= 70 ? 'bg-amber-100 text-amber-700' :
                                          'bg-red-100 text-red-700'
                                        }`}>
                                          {ev.confidence_score}%
                                        </div>
                                        <button 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            lineageDrawer.openDrawer({ 
                                              evidenceId: ev.id, 
                                              claimId: claim.id,
                                              sourceContext: 'claim' 
                                            });
                                          }}
                                          className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg bg-teal-50 text-teal-600 hover:bg-teal-100 transition-all"
                                          title="View Evidence Lineage"
                                          data-testid={`view-lineage-${ev.id}`}
                                        >
                                          <GitMerge size={14} />
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="text-center py-4 text-slate-500">
                                  <Shield size={24} className="mx-auto mb-2 opacity-50" />
                                  <p className="text-sm">No evidence linked yet</p>
                                </div>
                              )}
                              <button className="w-full mt-3 px-3 py-2 border border-dashed border-slate-300 text-slate-600 rounded-lg text-sm hover:bg-slate-50 flex items-center justify-center gap-2">
                                <Plus size={14} />
                                Link Evidence
                              </button>
                            </div>

                            {/* Module Links & Actions */}
                            <div className="bg-white rounded-lg p-4 border border-slate-200">
                              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                <Network size={16} />
                                Linked Modules
                              </h4>
                              <div className="space-y-2 mb-4">
                                {(claim.linked_modules || []).map((modKey, idx) => {
                                  const mod = MODULE_CONNECTIONS[modKey];
                                  if (!mod) return null;
                                  const ModIcon = mod.icon;
                                  return (
                                    <a key={idx} href={mod.path} className={`flex items-center gap-2 text-sm p-2 rounded-lg bg-${mod.color}-50 hover:bg-${mod.color}-100 transition-colors`}>
                                      <ModIcon size={14} className={`text-${mod.color}-600`} />
                                      <span className={`text-${mod.color}-700`}>{mod.name}</span>
                                      <ExternalLink size={12} className="ml-auto text-slate-400" />
                                    </a>
                                  );
                                })}
                              </div>

                              <h4 className="text-sm font-semibold text-slate-700 mb-3">Actions</h4>
                              <div className="space-y-2">
                                {claim.status === 'draft' && (
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); updateClaimStatus(claim.id, 'pending_review'); }}
                                    className="w-full px-3 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700"
                                    data-testid={`submit-review-${claim.id}`}
                                  >
                                    Submit for Review
                                  </button>
                                )}
                                {claim.status === 'pending_review' && (
                                  <>
                                    <button 
                                      onClick={(e) => { e.stopPropagation(); updateClaimStatus(claim.id, 'approved'); }}
                                      className="w-full px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                                      data-testid={`approve-${claim.id}`}
                                    >
                                      Approve Claim
                                    </button>
                                    <button 
                                      onClick={(e) => { e.stopPropagation(); updateClaimStatus(claim.id, 'rejected'); }}
                                      className="w-full px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
                                      data-testid={`reject-${claim.id}`}
                                    >
                                      Reject
                                    </button>
                                  </>
                                )}
                                {claim.status === 'approved' && (
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); updateClaimStatus(claim.id, 'published'); }}
                                    className="w-full px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-2"
                                    data-testid={`publish-${claim.id}`}
                                  >
                                    <Lock size={14} />
                                    Publish & Lock
                                  </button>
                                )}
                                <button className="w-full px-3 py-2 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">
                                  Add to Audit Pack
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Audit Trail */}
                          <div className="mt-4 p-3 bg-slate-100 rounded-lg">
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-4 text-slate-600">
                                <span>Last updated: {claim.updated_at || 'N/A'}</span>
                                <span>•</span>
                                <span>Owner: {claim.owner_name || 'N/A'}</span>
                                <span>•</span>
                                <span>Reviewer: {claim.reviewer_name || 'N/A'}</span>
                              </div>
                              <button className="text-blue-600 hover:text-blue-700 flex items-center gap-1">
                                <Clock size={14} />
                                View History
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Empty State */}
                {filteredClaims.length === 0 && (
                  <div className="bg-white rounded-xl shadow p-12 text-center" data-testid="empty-state">
                    <Shield size={48} className="mx-auto text-slate-300 mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">No Claims Found</h3>
                    <p className="text-slate-500 mb-4">Create your first claim to start building your audit pack.</p>
                    <button 
                      onClick={() => setShowCreateModal(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 inline-flex items-center gap-2"
                    >
                      <Plus size={18} />
                      New Claim
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold text-slate-900">EU Green Claims Directive Compliance Analytics</h3>
              
              {/* Criteria Coverage */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h4 className="font-semibold text-slate-700 mb-4">Criteria Coverage Across All Claims</h4>
                <div className="grid grid-cols-3 md:grid-cols-9 gap-4">
                  {GREEN_CLAIMS_CRITERIA.map(criteria => {
                    const claimsWithCriteria = claims.filter(c => (c.criteria_met || []).includes(criteria.id)).length;
                    const percentage = Math.round((claimsWithCriteria / (claims.length || 1)) * 100);
                    const Icon = criteria.icon;
                    return (
                      <div key={criteria.id} className="text-center">
                        <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-2 ${
                          percentage >= 80 ? 'bg-green-100' : percentage >= 50 ? 'bg-amber-100' : 'bg-red-100'
                        }`}>
                          <Icon size={24} className={
                            percentage >= 80 ? 'text-green-600' : percentage >= 50 ? 'text-amber-600' : 'text-red-600'
                          } />
                        </div>
                        <p className="text-lg font-bold text-slate-900">{percentage}%</p>
                        <p className="text-xs text-slate-500">{criteria.label}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Score Distribution */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-green-50 rounded-xl border border-green-200 p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle2 size={24} className="text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-700">{claims.filter(c => (c.green_claims_score || 0) >= 8).length}</p>
                      <p className="text-sm text-green-600">Audit Ready (8-9)</p>
                    </div>
                  </div>
                  <p className="text-xs text-green-700">Claims meeting 8+ of 9 EU GCD criteria</p>
                </div>
                <div className="bg-amber-50 rounded-xl border border-amber-200 p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <AlertTriangle size={24} className="text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-700">{claims.filter(c => (c.green_claims_score || 0) >= 5 && (c.green_claims_score || 0) < 8).length}</p>
                      <p className="text-sm text-amber-600">Needs Improvement (5-7)</p>
                    </div>
                  </div>
                  <p className="text-xs text-amber-700">Claims requiring additional evidence or verification</p>
                </div>
                <div className="bg-red-50 rounded-xl border border-red-200 p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <XCircle size={24} className="text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-700">{claims.filter(c => (c.green_claims_score || 0) < 5).length}</p>
                      <p className="text-sm text-red-600">High Risk (0-4)</p>
                    </div>
                  </div>
                  <p className="text-xs text-red-700">Claims at risk of greenwashing allegations</p>
                </div>
              </div>
            </div>
          )}

          {/* Intelligence Tab */}
          {activeTab === 'intelligence' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold text-slate-900">Inter-Module Intelligence</h3>
              <p className="text-slate-600">See how your claims connect to other modules in the Disclosure OS ecosystem.</p>
              
              {/* Module Connection Map */}
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 text-white">
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <Network size={20} />
                  Claims → Module Data Flow
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(MODULE_CONNECTIONS).slice(0, 8).map(([key, mod]) => {
                    const linkedCount = claims.filter(c => (c.linked_modules || []).includes(key)).length;
                    const ModIcon = mod.icon;
                    return (
                      <div key={key} className="bg-white/10 rounded-lg p-4 hover:bg-white/20 transition-colors">
                        <div className="flex items-center gap-2 mb-2">
                          <ModIcon size={18} className={`text-${mod.color}-400`} />
                          <span className="font-medium">{mod.name}</span>
                        </div>
                        <p className="text-2xl font-bold">{linkedCount}</p>
                        <p className="text-xs text-slate-400">claims linked</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Data Flow Diagram */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h4 className="font-semibold text-slate-700 mb-4">The Defensibility Chain</h4>
                <div className="flex items-center justify-between text-sm overflow-x-auto pb-4">
                  {[
                    { icon: Database, label: 'KPI Value', color: 'blue' },
                    { icon: Link2, label: 'Evidence', color: 'purple' },
                    { icon: Shield, label: 'Claim', color: 'green', highlight: true },
                    { icon: Target, label: 'Requirement', color: 'amber' },
                    { icon: FileText, label: 'Report', color: 'red' },
                    { icon: FileCheck, label: 'Audit Pack', color: 'emerald' }
                  ].map((item, idx) => {
                    const Icon = item.icon;
                    return (
                      <React.Fragment key={item.label}>
                        <div className={`flex flex-col items-center p-3 rounded-lg ${item.highlight ? 'bg-green-100 ring-2 ring-green-300' : 'bg-slate-50'}`}>
                          <Icon size={24} className={`text-${item.color}-500 mb-2`} />
                          <span className="text-slate-700 font-medium">{item.label}</span>
                        </div>
                        {idx < 5 && <ArrowRight size={20} className="text-slate-400 flex-shrink-0 mx-2" />}
                      </React.Fragment>
                    );
                  })}
                </div>
                <p className="text-sm text-slate-600 mt-4">
                  Every claim must trace back to source data through evidence. This chain is what makes your reports audit-defensible 
                  and compliant with EU Green Claims Directive requirements.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Claim Modal with EU Green Claims Validation */}
      {showCreateModal && (
        <CreateClaimModal 
          onClose={() => setShowCreateModal(false)}
          onSave={(newClaim) => {
            setClaims(prev => [...prev, { ...newClaim, id: `CLM-${String(prev.length + 1).padStart(3, '0')}` }]);
            setShowCreateModal(false);
          }}
        />
      )}
      
      {/* Evidence Lineage Drawer */}
      <LineageDrawer
        isOpen={lineageDrawer.isOpen}
        onClose={lineageDrawer.closeDrawer}
        evidenceId={lineageDrawer.evidenceId}
        claimId={lineageDrawer.claimId}
        sourceContext={lineageDrawer.sourceContext}
      />
    </div>
  );
};

// Create Claim Modal with EU Green Claims Directive Validation
const CreateClaimModal = ({ onClose, onSave, editClaim = null }) => {
  const [formData, setFormData] = useState(editClaim || {
    title: '',
    claim_text: '',
    category: 'Environmental',
    kpi_value: '',
    kpi_definition: '',
    frameworks: [],
    period_start: '',
    period_end: '',
    owner_name: '',
    status: 'draft'
  });
  const [bannedTermWarnings, setBannedTermWarnings] = useState([]);
  const [showWarnings, setShowWarnings] = useState(true);

  // Check for banned terms when claim text changes
  useEffect(() => {
    const warnings = checkBannedTerms(formData.claim_text);
    setBannedTermWarnings(warnings);
  }, [formData.claim_text]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!formData.title || !formData.claim_text) {
      alert('Please fill in required fields');
      return;
    }
    
    // Check if there are high severity banned terms
    const highSeverity = bannedTermWarnings.filter(w => w.severity === 'high');
    if (highSeverity.length > 0) {
      if (!window.confirm(`Warning: This claim contains ${highSeverity.length} prohibited term(s) under the EU Green Claims Directive. These may require modification before publication. Continue anyway?`)) {
        return;
      }
    }
    
    onSave({
      ...formData,
      updated_at: new Date().toISOString().split('T')[0],
      evidence_count: 0,
      evidence_strength: 'weak',
      green_claims_score: Math.max(0, 9 - bannedTermWarnings.length),
      verification_status: 'not_started',
      banned_terms_detected: bannedTermWarnings.length
    });
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-800' };
      case 'medium': return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-800' };
      case 'low': return { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800' };
      default: return { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', badge: 'bg-slate-100 text-slate-800' };
    }
  };

  const frameworkOptions = ['ESRS E1', 'ESRS E2', 'ESRS E3', 'ESRS E4', 'ESRS E5', 'ESRS S1', 'ESRS S2', 'ESRS S3', 'ESRS S4', 'ESRS G1', 'GRI 305', 'GRI 303', 'GRI 405', 'CDP Climate', 'CDP Water', 'TCFD', 'EU Taxonomy', 'CSDDD'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="create-claim-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield size={24} />
            <div>
              <h2 className="text-xl font-bold">{editClaim ? 'Edit Claim' : 'Create New Claim'}</h2>
              <p className="text-emerald-100 text-sm">EU Green Claims Directive compliant assertion builder</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
            <XCircle size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* EU Green Claims Compliance Banner */}
          {bannedTermWarnings.length > 0 && showWarnings && (
            <div className={`rounded-xl p-4 border-2 ${
              bannedTermWarnings.some(w => w.severity === 'high') 
                ? 'bg-red-50 border-red-200' 
                : bannedTermWarnings.some(w => w.severity === 'medium')
                ? 'bg-amber-50 border-amber-200'
                : 'bg-blue-50 border-blue-200'
            }`} data-testid="green-claims-warnings">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={24} className={
                    bannedTermWarnings.some(w => w.severity === 'high') ? 'text-red-500' : 'text-amber-500'
                  } />
                  <div>
                    <h4 className="font-bold text-slate-800">EU Green Claims Directive Warning</h4>
                    <p className="text-sm text-slate-600 mt-1">
                      {bannedTermWarnings.length} potentially problematic term(s) detected. These may require substantiation or modification.
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowWarnings(false)} className="text-slate-400 hover:text-slate-600">
                  <XCircle size={18} />
                </button>
              </div>
              
              <div className="mt-4 space-y-2">
                {bannedTermWarnings.map((warning, idx) => {
                  const colors = getSeverityColor(warning.severity);
                  return (
                    <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg ${colors.bg} border ${colors.border}`}>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${colors.badge}`}>
                        {warning.severity}
                      </span>
                      <span className="font-mono font-bold text-slate-800">"{warning.term}"</span>
                      <span className={`text-sm ${colors.text}`}>— {warning.reason}</span>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-4 p-3 bg-white/60 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>EU Green Claims Directive (2024/825):</strong> Generic environmental claims without specific, verifiable 
                  substantiation are prohibited. Consider rewording to include specific metrics, timeframes, and verification sources.
                </p>
              </div>
            </div>
          )}

          {/* Form Fields */}
          <div className="grid grid-cols-2 gap-6">
            {/* Claim Title */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">Claim Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="e.g., Total GHG Emissions (Scope 1 + 2)"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                data-testid="claim-title-input"
              />
            </div>

            {/* Claim Text */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Claim Statement *
                {bannedTermWarnings.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                    {bannedTermWarnings.length} warning(s)
                  </span>
                )}
              </label>
              <textarea
                value={formData.claim_text}
                onChange={(e) => handleChange('claim_text', e.target.value)}
                placeholder="Enter your disclosure claim statement. Avoid vague terms like 'eco-friendly' or 'sustainable' without specific metrics..."
                rows={4}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 ${
                  bannedTermWarnings.some(w => w.severity === 'high') 
                    ? 'border-red-300 bg-red-50' 
                    : bannedTermWarnings.length > 0 
                    ? 'border-amber-300 bg-amber-50' 
                    : 'border-slate-300'
                }`}
                data-testid="claim-text-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Best practice: Include specific numbers, timeframes, baselines, and verification sources.
              </p>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              >
                <option value="Environmental">Environmental</option>
                <option value="Social">Social</option>
                <option value="Governance">Governance</option>
              </select>
            </div>

            {/* KPI Value */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">KPI Value</label>
              <input
                type="text"
                value={formData.kpi_value}
                onChange={(e) => handleChange('kpi_value', e.target.value)}
                placeholder="e.g., 251,548 tCO2e"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            {/* Period */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Period Start</label>
              <input
                type="date"
                value={formData.period_start}
                onChange={(e) => handleChange('period_start', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Period End</label>
              <input
                type="date"
                value={formData.period_end}
                onChange={(e) => handleChange('period_end', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            {/* Frameworks */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">Linked Frameworks</label>
              <div className="flex flex-wrap gap-2">
                {frameworkOptions.map(fw => (
                  <button
                    key={fw}
                    type="button"
                    onClick={() => {
                      const current = formData.frameworks || [];
                      if (current.includes(fw)) {
                        handleChange('frameworks', current.filter(f => f !== fw));
                      } else {
                        handleChange('frameworks', [...current, fw]);
                      }
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      (formData.frameworks || []).includes(fw)
                        ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-300'
                        : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
                    }`}
                  >
                    {fw}
                  </button>
                ))}
              </div>
            </div>

            {/* Owner */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">Claim Owner</label>
              <input
                type="text"
                value={formData.owner_name}
                onChange={(e) => handleChange('owner_name', e.target.value)}
                placeholder="e.g., Sustainability Team"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          {/* Green Claims Compliance Score Preview */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Scale size={18} />
              EU Green Claims Compliance Preview
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-white rounded-lg">
                <p className="text-3xl font-bold text-emerald-600">{Math.max(0, 9 - bannedTermWarnings.length)}/9</p>
                <p className="text-xs text-slate-500">Compliance Score</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg">
                <p className={`text-3xl font-bold ${bannedTermWarnings.filter(w => w.severity === 'high').length > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                  {bannedTermWarnings.filter(w => w.severity === 'high').length}
                </p>
                <p className="text-xs text-slate-500">High-Risk Terms</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg">
                <p className={`text-3xl font-bold ${bannedTermWarnings.length > 0 ? 'text-amber-500' : 'text-emerald-500'}`}>
                  {bannedTermWarnings.length === 0 ? 'Ready' : 'Review'}
                </p>
                <p className="text-xs text-slate-500">Status</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-600 hover:text-slate-900 font-medium"
          >
            Cancel
          </button>
          <div className="flex items-center gap-3">
            {bannedTermWarnings.length > 0 && (
              <span className="text-sm text-amber-600 flex items-center gap-1">
                <AlertTriangle size={16} />
                {bannedTermWarnings.length} warning(s)
              </span>
            )}
            <button
              onClick={handleSubmit}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 flex items-center gap-2"
              data-testid="save-claim-btn"
            >
              <CheckCircle2 size={18} />
              {editClaim ? 'Update Claim' : 'Create Claim'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimsAssertions;
